import { io } from "socket.io-client";

// Use dynamic URL from api.js (same as trader WebSocket)
const getWsUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.hostname;
  const port = import.meta.env.VITE_WS_PORT || '3006';
  
  // For localhost, use the port
  if (host === 'localhost' || host === '127.0.0.1') {
    return `http://${host}:${port}`;
  }
  
  // For production, use the same host with WS port
  return `${window.location.protocol}//${host}:${port}`;
};

const WS_URL = getWsUrl();

/**
 * Broker WebSocket Service - Real-Time Monitoring for All Accounts
 * 
 * Automatically receives real-time updates for ALL accounts under the brokerage:
 * - account_update      → P/L snapshots from P/L Engine (10Hz) for all accounts
 * - position:opened     → When any client opens a position
 * - position:closed     → When any client closes a position
 * - position:modified   → When any client modifies SL/TP
 * - positions:update    → Initial positions for all accounts on connect
 * - margin:warning      → Margin call alerts across all accounts
 * - margin:stop_out     → Stop-out notifications across all accounts
 * - price:update        → Live price ticks for subscribed symbols
 * - broker:stats        → Broker-wide statistics
 * - broker:exposure     → Broker exposure data
 * - order:placed        → When any client places an order
 * - order:cancelled     → When any order is cancelled
 * - order:filled        → When any order is filled
 * - order:modified      → When any order is modified
 */
class BrokerWebSocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.subscribedSymbols = new Set();

    // Reconnection management
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
  }

  // ============================================================================
  // CONNECTION
  // ============================================================================

  connect(token) {
    if (this.socket?.connected) {
      console.log('🔌 Broker Admin already connected to WebSocket');
      return;
    }

    console.log('🔌 Broker Admin connecting to WebSocket server...', WS_URL);
    console.log('🔑 Token present:', !!token);
    console.log('🔑 Token preview:', token ? `${token.substring(0, 20)}...` : 'null');

    this.socket = io(WS_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30000,
      timeout: 20000,
      pingTimeout: 60000,
      pingInterval: 25000,
    });

    this.socket.on("connect", () => {
      console.log('✅ Broker Admin WebSocket connected - ID:', this.socket.id);
      console.log('🚀 Transport:', this.socket.io.engine.transport.name);
      this.isConnected = true;
      this.reconnectAttempts = 0;

      // Resubscribe to symbols after reconnection
      if (this.subscribedSymbols.size > 0) {
        const symbols = Array.from(this.subscribedSymbols);
        console.log('🔄 Resubscribing to symbols:', symbols);
        this.socket.emit("subscribe:prices", { symbols });
      }

      this.emit("connected", true);
    });

    this.socket.on("disconnect", (reason) => {
      console.log('❌ Broker Admin WebSocket disconnected:', reason);
      this.isConnected = false;
      this.emit("connected", false);
    });

    this.socket.on("connect_error", (error) => {
      console.error('❌ WebSocket connection error:', error);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error type:', error.type);
      this.isConnected = false;
      this.emit("connected", false);
    });

    this.socket.on("error", (error) => {
      console.error('❌ WebSocket error:', error);
      this.emit("error", error);
    });

    // Debug logging
    if (import.meta.env.DEV) {
      this.socket.onAny((event, data) => {
        console.log('📨 Broker WS Event:', event, data);
      });

      this.socket.io.on("ping", () => {
        console.log('🏓 ping');
      });

      this.socket.io.on("pong", (latency) => {
        console.log('🏓 pong - latency:', latency, 'ms');
      });
    }

    this.setupListeners();
  }

  setupListeners() {
    console.log('🎧 Setting up Broker WebSocket event listeners...');

    // Remove any existing listeners to prevent duplicates
    this.socket.off("positions:update");
    this.socket.off("position:opened");
    this.socket.off("position:closed");
    this.socket.off("position:modified");
    this.socket.off("position:sl_triggered");
    this.socket.off("position:tp_triggered");
    this.socket.off("account:update");
    this.socket.off("account_update");
    this.socket.off("account:balance");
    this.socket.off("margin:warning");
    this.socket.off("margin:stop_out");
    this.socket.off("account:margin_call");
    this.socket.off("account:liquidated");
    this.socket.off("order:placed");
    this.socket.off("order:cancelled");
    this.socket.off("order:modified");
    this.socket.off("order:filled");
    this.socket.off("price:update");
    this.socket.off("price:subscribed");
    this.socket.off("candle:update");
    this.socket.off("candle:subscribed");
    this.socket.off("broker:stats");
    this.socket.off("broker:exposure");
    this.socket.off("broker:alert");
    this.socket.off("client:login");
    this.socket.off("client:logout");
    this.socket.off("notification");
    this.socket.off("order:error");
    this.socket.off("position:error");
    this.socket.off("admin:error");

    // ============================================================================
    // POSITION EVENTS (ALL ACCOUNTS)
    // ============================================================================

    // Initial positions for all accounts on connect
    this.socket.on("positions:update", (data) => {
      console.log('📋 All positions received:', data);
      this.emit("positions:update", data);
    });

    this.socket.on("position:opened", (data) => {
      console.log("🟢 Position opened:", {
        account: data.account_id,
        symbol: data.symbol,
        side: data.side,
        volume: data.volume
      });
      this.emit("position:opened", data);
    });

    this.socket.on("position:closed", (data) => {
      console.log("🔴 Position closed:", {
        account: data.account_id,
        symbol: data.symbol,
        pnl: data.net_profit || data.pnl
      });
      this.emit("position:closed", data);
    });

    this.socket.on("position:modified", (data) => {
      console.log("✏️ Position modified:", {
        account: data.account_id,
        position: data.position_id
      });
      this.emit("position:modified", data);
    });

    this.socket.on("position:sl_triggered", (data) => {
      console.log("🎯 Stop Loss triggered:", {
        account: data.account_id,
        symbol: data.symbol,
        loss: data.pnl
      });
      this.emit("position:sl_triggered", data);
    });

    this.socket.on("position:tp_triggered", (data) => {
      console.log("🎯 Take Profit triggered:", {
        account: data.account_id,
        symbol: data.symbol,
        profit: data.pnl
      });
      this.emit("position:tp_triggered", data);
    });

    // ============================================================================
    // ORDER EVENTS (ALL ACCOUNTS)
    // ============================================================================

    this.socket.on("order:placed", (data) => {
      console.log("✅ Order placed:", {
        account: data.account_id,
        symbol: data.symbol,
        type: data.type
      });
      this.emit("order:placed", data);
    });

    this.socket.on("order:cancelled", (data) => {
      console.log("❌ Order cancelled:", {
        account: data.account_id,
        order: data.order_id
      });
      this.emit("order:cancelled", data);
    });

    this.socket.on("order:modified", (data) => {
      console.log("✏️ Order modified:", {
        account: data.account_id,
        order: data.order_id
      });
      this.emit("order:modified", data);
    });

    this.socket.on("order:filled", (data) => {
      console.log("✅ Order filled:", {
        account: data.account_id,
        order: data.order_id
      });
      this.emit("order:filled", data);
    });

    // ============================================================================
    // ACCOUNT EVENTS (ALL ACCOUNTS)
    // ============================================================================

    // Real-time P/L snapshots from P/L Engine (10Hz) for ALL accounts
    this.socket.on("account_snapshot", (snapshot) => {
      // snapshot includes: { account_id, balance, equity, margin_used, free_margin, margin_level, credit, positions: [...] }
      this.emit("account_snapshot", snapshot);
    });
    
    // Also listen for account_update (legacy/fallback)
    this.socket.on("account_update", (snapshot) => {
      this.emit("account_snapshot", snapshot);
    });

    // Balance updates from database (less frequent)
    this.socket.on("account:update", (data) => {
      console.log('💰 Account balance updated:', {
        account: data.account_id,
        balance: data.balance
      });
      this.emit("account:update", data);
    });

    this.socket.on("account:balance", (data) => {
      console.log("💰 Balance update:", {
        account: data.account_id,
        balance: data.balance
      });
      this.emit("account:balance", data);
    });

    // ============================================================================
    // MARGIN EVENTS (ALL ACCOUNTS - from Risk Engine)
    // ============================================================================

    this.socket.on("margin:warning", (data) => {
      console.warn("⚠️ MARGIN CALL:", {
        account: data.account_id,
        level: data.margin_level
      });
      this.emit("margin:warning", data);
    });

    this.socket.on("margin:stop_out", (data) => {
      console.error("🚨 STOP OUT:", {
        account: data.account_id,
        level: data.margin_level,
        positions_closed: data.positions_closed?.length
      });
      this.emit("margin:stop_out", data);
    });

    this.socket.on("account:margin_call", (data) => {
      console.warn("⚠️ MARGIN CALL (legacy):", {
        account: data.account_id,
        level: data.margin_level
      });
      this.emit("account:margin_call", data);
    });

    this.socket.on("account:liquidated", (data) => {
      console.error("🚨 ACCOUNT LIQUIDATED:", data.account_id);
      this.emit("account:liquidated", data);
    });

    // ============================================================================
    // PRICE EVENTS
    // ============================================================================
    
    this.socket.on("price:update", (data) => {
      this.emit("price:update", data);
    });

    this.socket.on("price:subscribed", (data) => {
      console.log("✅ Subscribed to prices:", data);
      this.emit("price:subscribed", data);
    });

    // ============================================================================
    // CANDLE EVENTS
    // ============================================================================
    
    this.socket.on('candle:update', (data) => {
      this.emit('candle:update', data);
    });

    this.socket.on('candle:subscribed', (data) => {
      console.log('✅ Candles subscribed:', data);
      this.emit('candle:subscribed', data);
    });

    // ============================================================================
    // BROKER-SPECIFIC EVENTS
    // ============================================================================

    this.socket.on("broker:stats", (data) => {
      console.log("📊 Broker stats update:", data);
      this.emit("broker:stats", data);
    });

    this.socket.on("broker:exposure", (data) => {
      console.log("📊 Broker exposure update:", data);
      this.emit("broker:exposure", data);
    });

    this.socket.on("broker:alert", (data) => {
      console.warn("🔔 Broker alert:", data.message);
      this.emit("broker:alert", data);
    });

    // ============================================================================
    // CLIENT ACTIVITY
    // ============================================================================

    this.socket.on("client:login", (data) => {
      console.log("👤 Client logged in:", {
        email: data.email,
        account: data.account_id
      });
      this.emit("client:login", data);
    });

    this.socket.on("client:logout", (data) => {
      console.log("👤 Client logged out:", {
        email: data.email,
        account: data.account_id
      });
      this.emit("client:logout", data);
    });

    // ============================================================================
    // NOTIFICATIONS
    // ============================================================================

    this.socket.on("notification", (data) => {
      console.log("🔔 Notification:", data);
      this.emit("notification", data);
    });

    // ============================================================================
    // ERRORS
    // ============================================================================

    this.socket.on("order:error", (data) => {
      console.error("❌ Order error:", data);
      this.emit("order:error", data);
    });

    this.socket.on("position:error", (data) => {
      console.error("❌ Position error:", data);
      this.emit("position:error", data);
    });

    this.socket.on("admin:error", (data) => {
      console.error("❌ Admin error:", data);
      this.emit("admin:error", data);
    });
  }

  // ============================================================================
  // ADMIN-SPECIFIC METHODS
  // ============================================================================

  /**
   * Request real-time broker statistics
   */
  requestBrokerStats() {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot request stats: Socket not connected");
      return;
    }
    
    console.log("📊 Requesting broker stats");
    this.socket.emit("admin:get_broker_stats");
  }

  /**
   * Request real-time exposure data
   */
  requestExposure() {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot request exposure: Socket not connected");
      return;
    }
    
    console.log("📊 Requesting exposure data");
    this.socket.emit("admin:get_exposure");
  }

  /**
   * Force close a position (admin override)
   * @param {number} accountId 
   * @param {number} positionId 
   */
  adminClosePosition(accountId, positionId) {
    if (!this.socket?.connected) {
      throw new Error("WebSocket not connected");
    }
    
    console.log("🔒 Admin closing position:", positionId, "Account:", accountId);
    this.socket.emit("admin:close_position", { accountId, positionId });
  }

  /**
   * Force cancel an order (admin override)
   * @param {number} accountId 
   * @param {number} orderId 
   */
  adminCancelOrder(accountId, orderId) {
    if (!this.socket?.connected) {
      throw new Error("WebSocket not connected");
    }
    
    console.log("🔒 Admin cancelling order:", orderId, "Account:", accountId);
    this.socket.emit("admin:cancel_order", { accountId, orderId });
  }

  /**
   * Manually trigger margin check for an account (testing/admin)
   * @param {number} accountId 
   */
  triggerMarginCheck(accountId) {
    if (!this.socket?.connected) {
      throw new Error("WebSocket not connected");
    }
    
    console.log("🔍 Triggering margin check for account:", accountId);
    this.socket.emit("admin:trigger_margin_check", { accountId });
  }

  // ============================================================================
  // PRICE SUBSCRIPTION
  // ============================================================================

  subscribePrices(symbols) {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot subscribe: Socket not connected");
      return;
    }

    const newSymbols = symbols.filter(s => !this.subscribedSymbols.has(s));
    
    if (newSymbols.length === 0) return;

    console.log("📤 Subscribing to prices:", newSymbols);
    
    newSymbols.forEach(symbol => this.subscribedSymbols.add(symbol));
    this.socket.emit("subscribe:prices", { symbols: newSymbols });
  }

  unsubscribePrices(symbols) {
    if (!this.socket?.connected) return;

    const toUnsubscribe = symbols.filter(s => this.subscribedSymbols.has(s));
    
    if (toUnsubscribe.length === 0) return;

    console.log("📤 Unsubscribing from prices:", toUnsubscribe);
    
    toUnsubscribe.forEach(symbol => this.subscribedSymbols.delete(symbol));
    this.socket.emit("unsubscribe:prices", { symbols: toUnsubscribe });
  }

  getSubscribedSymbols() {
    return Array.from(this.subscribedSymbols);
  }

  // ============================================================================
  // CANDLE SUBSCRIPTION
  // ============================================================================

  subscribeCandles(symbol, timeframe) {
    if (!this.socket?.connected) {
      console.warn("⚠️ Cannot subscribe to candles: Socket not connected");
      return;
    }

    console.log(`📊 Subscribing to candles: ${symbol} ${timeframe}`);
    this.socket.emit("candle:subscribe", { symbol, timeframe });
  }

  unsubscribeCandles(symbol, timeframe) {
    if (!this.socket?.connected) return;

    console.log(`📊 Unsubscribing from candles: ${symbol} ${timeframe}`);
    this.socket.emit("candle:unsubscribe", { symbol, timeframe });
  }

  // ============================================================================
  // EVENT EMITTER (Internal)
  // ============================================================================

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
    
    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (!this.listeners.has(event)) return;
    const callbacks = this.listeners.get(event);
    const index = callbacks.indexOf(callback);
    if (index > -1) {
      callbacks.splice(index, 1);
    }
  }

  emit(event, data) {
    if (!this.listeners.has(event)) return;
    
    const callbacks = this.listeners.get(event);
    callbacks.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${event} callback:`, error);
      }
    });
  }

  // ============================================================================
  // STATUS & DIAGNOSTICS
  // ============================================================================

  getStatus() {
    return {
      connected: this.isConnected,
      socketId: this.socket?.id,
      transport: this.socket?.io?.engine?.transport?.name,
      subscribedSymbols: this.getSubscribedSymbols(),
      reconnectAttempts: this.reconnectAttempts,
    };
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting broker admin WebSocket');
      this.socket.disconnect();
      this.socket = null;
    }

    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.listeners.clear();
    this.subscribedSymbols.clear();
  }
}

// Create singleton instance
const brokerWsService = new BrokerWebSocketService();

export default brokerWsService;